'use client';

import {
  Box,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  PathType,
  SecurityScanExampleTypeEnum,
  type InventoryResource,
  type SecurityScan,
  type SecurityScanResult,
} from '@repo/api-gw-sdk';
import { Fragment, useState } from 'react';

import { useViewParameters } from '@/contexts/navigation';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BaseAction } from '@/data/inventory/actions';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { isDateValid } from '@/utils/dateTime';
import { dayjs, DATE_FORMAT } from '@/utils/dayjs';

import { ExcludeFromRansomwareDialog } from './excludeFromRansomwareDialog';
import { getEvidenceStrings } from './securityUtils';
import { SkullIcon } from './skullIcon';

import { Accordion, AccordionDetails, AccordionSummary } from '../accordion';
import { PathLink } from '../explorer/fileLink';
import { StatusCardHeader } from '../panels/instance/tabs/overview-tab/statusCardHeader';
import { StatusCardWrapper } from '../panels/instance/tabs/overview-tab/statusCardWrapper';
import { Panels } from '../panels/panels';
import { SnapshotIndicator } from '../snapshots/SnapshotIndicator';
import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';

interface SecurityScanCardProps {
  resource: InventoryResource;
  onExclusionChanged: () => Promise<void>;
}

export const SecurityScanCard = (props: SecurityScanCardProps) => {
  const { resource, onExclusionChanged } = props;
  const dal = useDAL();
  const { isAuthorizedResource } = useRoles();
  const { rightPanel } = useWorkspace();
  const { removeParam } = useViewParameters();
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const { data: scans, isLoading: isLoadingScans } =
    dal.security.useScansSummary(resource.id);
  const snapshotId = scans?.lastClean?.snapshotId;
  const snapshot =
    dal.inventory.snapshots.useSnapshot(snapshotId).data?.snapshot;

  const evidences: SecurityScanResult[] = scans?.lastScan?.results || [];
  const actions: BaseAction[] = [
    {
      title: 'Exclude from ransomware detection',
      execute: () => {
        setExcludeDialogOpen(true);
      },
      disabled: () =>
        !isAuthorizedResource('update:exclude_resource', resource),
      id: 'exclude-security-alert',
      tooltip: '',
      icon: '',
    },
  ];

  const description = `${scans?.infectedSnapshotsCount || 0} ${scans?.infectedSnapshotsCount === 1 ? 'snapshot has' : 'snapshots have'} ransomware infection indicators.`;
  async function excludeAlerts(): Promise<void> {
    await dal.security.excludeResource(resource.id);
    await onExclusionChanged();
    setExcludeDialogOpen(false);
  }

  const onRestore = () => {
    if (!snapshot) {
      return;
    }
    const vols = snapshot.resource?.properties?.awsEc2?.volumes || [];

    const onClose = () => {
      rightPanel.setIsOpen(false);
      removeParam('id');
    };

    const onBack = () => {
      rightPanel.setComponent({
        panel: Panels.InventoryInstance,
        props: {
          id: resource.id,
          onClose,
          initialTab: 'overview',
        },
      });
    };

    rightPanel.setComponent({
      panel: Panels.RestoreEc2InstanceWizard,
      props: {
        snapshot: snapshot,
        resourceName: resource.resourceName,
        volumes: vols,
        onClose,
        onBack,
      },
    });
  };

  return (
    <StatusCardWrapper borderColor='var(--mui-palette-error-main)'>
      <Stack gap='24px'>
        <Stack marginBottom='12px' alignItems={'center'} spacing={'12px'}>
          <Box width='100%' padding='0 24px 0 40px'>
            <StatusCardHeader
              title='Security Issues Detected'
              actions={actions}
              entity={resource}
            />
          </Box>
          <SkullIcon />
          <Typography className='font-semibold' fontSize={'16px'}>
            Infected snapshots
          </Typography>
        </Stack>
        <Stack direction='row' marginY='12px' marginX='40px' gap='40px'>
          {isLoadingScans ? (
            <Stack gap='4px'>
              <Skeleton width='250px' animation='wave' />
              <Skeleton width='200px' animation='wave' />
              <Skeleton width='230px' animation='wave' />
            </Stack>
          ) : (
            <Typography
              variant='subtitle1'
              width='250px'
              lineHeight='20px'
              paddingTop='6px'
            >
              {description}
            </Typography>
          )}
          <Divider orientation='vertical' flexItem />
          <Stack flexGrow={1} gap='12px' justifyContent='space-around'>
            {scans?.lastClean && isDateValid(scans.lastClean.createdTime) ? (
              <SnapshotSummary
                resource={resource}
                scan={scans.lastClean}
                title='Latest clean snapshot'
                onRestore={onRestore}
                showRestoreButton
              />
            ) : (
              <EmptySnapshotSummary title='Latest clean snapshot' />
            )}
            {scans?.firstInfected &&
            isDateValid(scans.firstInfected.createdTime) ? (
              <SnapshotSummary
                resource={resource}
                scan={scans.firstInfected}
                title='First infected snapshot'
              />
            ) : (
              <EmptySnapshotSummary title='First infected snapshot' />
            )}
          </Stack>
        </Stack>
        <Divider sx={{ margin: '0 40px', borderStyle: 'dashed' }} />
        <Typography paddingX='40px' variant='subtitle1'>
          {`Suspicious indicators from the latest snapshot on ${dayjs.utc(scans?.lastScan?.createdTime).format(DATE_FORMAT)}:`}
        </Typography>
        <Stack marginY='12px' gap='8px'>
          {isLoadingScans ? (
            <Skeleton
              sx={{ marginLeft: '40px', marginRight: '24px' }}
              animation='wave'
            />
          ) : (
            evidences.map((evidence, idx) => {
              if (!evidence.examples?.length) {
                return null;
              }
              return (
                <Fragment key={evidence.evidenceType}>
                  {!!idx && <Divider />}
                  <EvidenceDetails evidence={evidence} resource={resource} />
                </Fragment>
              );
            })
          )}
        </Stack>
      </Stack>
      <ExcludeFromRansomwareDialog
        isOpen={excludeDialogOpen}
        onCancel={() => setExcludeDialogOpen(false)}
        triggerExclusion={excludeAlerts}
        resourceName={resource.resourceName}
      />
    </StatusCardWrapper>
  );
};

interface EvidenceDetailsProps {
  evidence: SecurityScanResult;
  resource: InventoryResource;
}

const EvidenceDetails = ({ evidence, resource }: EvidenceDetailsProps) => {
  const evidenceStrings = getEvidenceStrings(evidence.evidenceType);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Accordion
      expanded={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      disableGutters
      sx={{ marginX: '40px' }}
    >
      <AccordionSummary>
        <Stack width='100%' direction='row' justifyContent='space-between'>
          <Typography variant='h4'>{evidenceStrings.label}</Typography>
          {!isOpen && <Typography variant='h4'>{evidence.count}</Typography>}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography marginBottom='16px'>{`${evidence.count} ${evidenceStrings.entity}${evidence.count === 1 ? ' was' : 's were'} ${evidenceStrings.description}. Here are some samples:`}</Typography>
        <Stack gap='16px'>
          {evidence.examples?.map((example) => (
            <Stack
              direction={'row'}
              gap='12px'
              key={example.examplePath}
              alignItems='center'
            >
              <SkullIcon />
              <PathLink
                className='!text-[var(--mui-palette-error-main)] [&:hover]:!text-[var(--mui-palette-error-light)]'
                entity={resource}
                path={example.examplePath}
                pathType={
                  example.type === SecurityScanExampleTypeEnum.Directory
                    ? PathType.Directory
                    : PathType.File
                }
              />
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const SnapshotSummary = (props: {
  resource: InventoryResource;
  scan: SecurityScan;
  title: string;
  onRestore?: () => void;
  showRestoreButton?: boolean;
}) => {
  const { scan, title, showRestoreButton, onRestore, resource } = props;

  const dal = useDAL();
  const { isAuthorizedResource } = useRoles();
  const { vaults } = useBackupVaults();
  const { data: snapshot, isLoading } = dal.inventory.snapshots.useSnapshot(
    scan.snapshotId
  );
  const vault = vaults.find((v) => v.id === snapshot?.snapshot.vaultId);

  if (isLoading) {
    return <Skeleton />;
  }

  const isAuthToRestore = isAuthorizedResource(
    'create:restore_resource',
    resource
  );

  return (
    <Stack
      gap='12px'
      direction='row'
      alignItems='center'
      justifyContent='space-between'
    >
      <Typography width='160px'>{title}</Typography>
      <Stack direction='row' gap='8px' alignItems='center'>
        <SnapshotIndicator
          sx={{ margin: '4px' }}
          backgroundColor={vault?.backgroundColor || null}
        />
        <Typography>
          {dayjs.utc(scan.createdTime).format(DATE_FORMAT)}
        </Typography>
        {showRestoreButton ? (
          <AuthorizationTooltip title='Restore' isAuthorized={isAuthToRestore}>
            <span>
              <IconButton
                disabled={!isAuthToRestore}
                color='primary'
                size='small'
                onClick={onRestore}
              >
                <i className='material-symbols-settings-backup-restore-rounded' />
              </IconButton>
            </span>
          </AuthorizationTooltip>
        ) : (
          <Box width='32px' />
        )}
      </Stack>
    </Stack>
  );
};

const EmptySnapshotSummary = (props: { title: string }) => {
  const { title } = props;
  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Typography flexGrow={1}>{title}</Typography>
      <Typography marginRight='40px'>None</Typography>
    </Stack>
  );
};
